import Grid from '@material-ui/core/Grid';
import GroupWorkIcon from '@material-ui/icons/GroupWork';
import LabelIcon from '@material-ui/icons/Label';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { APPLICATION_PATHS } from '../../application-core/constants';
import InAppBar from '../../core/InAppBar';
import { processHashLocation } from '../../core/utils/utils';
import Warning from '../../core/Warning';
import GS from '../../grafsync/grafsync';
import FloatingConceptView from '../floating/FloatingConceptView';
import FloatingLabelView from '../floating/FloatingLabelView';

const tabValues = {
  0: "concept",
  1: "label"
};

class FloatingEditorContainer extends Component {

  constructor(props) {
    super(props);
    this.state = {
      tabValue: 0,
      updateAt: null,
      hasScrolled: false,
      viewLang: props.defaultLang,
      deletedLabels: [],
      deletedConcepts: [],
      activeLabel: null,
      activeConcept: null,
      foundPath: null,
      isErrored: false
    };
  }

  static getDerivedStateFromProps(props, state) {
    const paramsMap = processHashLocation(props.location);
    const concept = paramsMap.get("concept");
    const label = paramsMap.get("label");
    const view = paramsMap.get("view");
    const tab = view === "concept" || !props.skosXL ? 0 : 1;
    if ((tab !== state.tabValue) || (label !== state.activeLabel) || (concept !== state.activeConcept)) {
      return {
        ...(view) && { tabValue: tab },
        ...(state.activeConcept !== concept) &&
        { activeConcept: (concept === undefined || concept === null || concept === "") ? null : concept },
        ...(state.activeLabel !== label) &&
        { activeLabel: (label === undefined || label === null || label === "") ? null : label }
      };
    }
    return null;
  }

  buildLink(view, concept, label) {
    let link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/floating#project=' + this.props.project + "&view=" + view;
    link += concept !== null ? "&concept=" + concept : "";
    link += label !== null ? "&label=" + label : "";
    return link;
  }

  changeTab = (tabValue) => {
    const view = tabValues[tabValue];
    const link = this.buildLink(view, this.state.activeConcept, this.state.activeLabel);
    this.props.history.push(link);
  };

  onExitWarning = () => this.setState({ isErrored: false });

  componentDidMount() {
    if (this.state.activeConcept && this.state.tabValue === 0) this.setFoundConcept(this.state.activeConcept);
  }

  setFoundConcept = async (iri) => {
    this.showIsolatedDetail(iri);
  };

  showIsolatedDetail = (iri) => {
    const link = this.buildLink("concept", iri, this.state.activeLabel);
    const currentPath = this.props.location.pathname + this.props.location.hash;
    if (currentPath !== link) this.props.history.push(link);
  };

  deleteConcept = (iri) => {
    GS.deleteResource(iri, this.props.graph);
    const deletedConceptsList = [...this.state.deletedConcepts];
    deletedConceptsList.push(iri);
    this.setState({
      updateAt: Date.now(),
      deletedConcepts: deletedConceptsList
    });
    this.showIsolatedDetail(null);
  };

  deleteLabel = (iri, conceptId, label, lang, labelProperty) => {
    // If label is used by a concept as its prefLabel and it is the default language we need to change the concepts to use prefLabel
    if (conceptId && labelProperty === "prefLabel" && lang === this.props.defaultLang) {
      GS.replace(conceptId, this.props.graph, "xlPrefLabel", iri, null, null, "prefLabel", label, lang);
    }
    GS.deleteResource(iri, this.props.graph);
    const deletedLabelList = [...this.state.deletedLabels];
    deletedLabelList.push(iri);
    this.setState({ updateAt: Date.now(), deletedLabels: deletedLabelList });
    this.showLabelDetail(null);
  };

  showLabelDetail = (iri) => {
    const link = this.buildLink("label", this.state.activeConcept, iri);
    const currentPath = this.props.location.pathname + this.props.location.hash;
    if (currentPath !== link) this.props.history.push(link);
  };

  showFloatingLabel = (iri) => {
    const link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/floating#project=' + this.props.project + "&view=label&label=" + iri;
    const currentPath = this.props.location.pathname + this.props.location.hash;
    if (currentPath !== link) this.props.history.push(link);
  };

  setViewLanguage = (lang) => this.setState({ viewLang: lang });

  render() {

    const { activeLabel, deletedLabels, deletedConcepts, updateAt, tabValue, isErrored, activeConcept, message, viewLang } = this.state;
    const { skosXL, defaultLang, project, graph, entitlements, ontologyProjects, projectUsers } = this.props;

    if (isErrored) return <Warning message={message} onExitWarning={this.onExitWarning} />;

    let tabItems = [{
      icon: GroupWorkIcon,
      label: "Concepts"
    }];
    if (skosXL) tabItems.push({
      icon: LabelIcon,
      label: "Labels"
    });

    const appBar = <InAppBar
      tabValue={tabValue}
      onChangeTab={this.changeTab}
      tabItems={tabItems} />;

    return (
      <Grid container spacing={0}>
        <Grid item xs={12}>
          {tabValue === 0 &&
            <FloatingConceptView
              appBar={appBar}
              graph={graph}
              entitlements={entitlements}
              isReadOnly={true}
              updateAt={updateAt}
              project={project}
              activeConcept={activeConcept}
              defaultLang={defaultLang}
              projectUsers={projectUsers}
              viewLang={viewLang}
              skosXL={skosXL}
              onSelect={this.showIsolatedDetail}
              ontologyProjects={ontologyProjects}
              onSelectViewLanguage={this.setViewLanguage}
              deletedConcepts={deletedConcepts}
              onDeleteConcept={this.deleteConcept}
              showFloatingLabel={this.showFloatingLabel}
              showLabelDetail={this.showLabelDetail}
            />
          }
          {tabValue === 1 &&
            <FloatingLabelView
              appBar={appBar}
              graph={graph}
              entitlements={entitlements}
              isReadOnly={true}
              updateAt={updateAt}
              project={project}
              defaultLang={defaultLang}
              activeLabel={activeLabel}
              onSelect={this.showLabelDetail}
              deletedLabels={deletedLabels}
              onDeleteLabel={this.deleteLabel}
            />
          }
        </Grid>
      </Grid>
    );
  }

}

FloatingEditorContainer.propTypes = {
  entitlements: PropTypes.array.isRequired,
  history: PropTypes.object,
  skosXL: PropTypes.bool.isRequired,
  ontologyProjects: PropTypes.array,
  graph: PropTypes.string.isRequired,
  projectUsers: PropTypes.array,
  project: PropTypes.string.isRequired,
  defaultLang: PropTypes.string.isRequired
};

export default withRouter(FloatingEditorContainer);